import { ImageSource, Texture } from 'pixi.js';

/**
 * Each world's floor on the realm map: the reel floor of the world's first
 * slot, so a world's board, the switcher naming it and its slots' name
 * plates sit on that world's own ground (producer 2026-10-19: "the
 * background texture for these should match the land texture").
 *
 * A floor loads when first wanted, one at a time, so a glide across the
 * map never starts seven downloads at once. The textures are the map's
 * own, outside Pixi's Assets cache: the map frees them when it goes (they
 * are big, and the slot is next) without touching a copy the rest of the
 * game loaded from the same file.
 */
export class WorldFloors {
  private readonly textures = new Map<number, Texture>();
  private readonly loads = new Map<number, Promise<Texture | null>>();
  private queue: Promise<unknown> = Promise.resolve();
  private gone = false;

  constructor(
    /** A world's floor (index into BIOMES), or undefined when it has none. */
    private readonly urlOf: (world: number) => string | undefined,
    /** A world's floor has arrived. */
    private readonly onLoad: (world: number, texture: Texture) => void,
  ) {}

  /** The world's floor if it has loaded, else null. Starts nothing. */
  loaded(world: number): Texture | null {
    return this.textures.get(world) ?? null;
  }

  /** The world (index into BIOMES) whose floor `texture` is, or null. */
  worldOf(texture: Texture | null): number | null {
    if (!texture) return null;
    for (const [world, t] of this.textures) if (t === texture) return world;
    return null;
  }

  /** The world's floor; resolves with null when it has none or it fails.
   *  Each world loads once. */
  load(world: number): Promise<Texture | null> {
    const known = this.loads.get(world);
    if (known) return known;
    const url = this.urlOf(world);
    if (!url) {
      const none = Promise.resolve(null);
      this.loads.set(world, none);
      return none;
    }
    const run = this.queue.then(() => this.fetch(world, url));
    this.queue = run.catch(() => undefined);
    this.loads.set(world, run);
    return run;
  }

  /** Free every floor. Loads still running come to nothing. */
  destroy(): void {
    this.gone = true;
    for (const t of this.textures.values()) t.destroy(true);
    this.textures.clear();
  }

  private async fetch(world: number, url: string): Promise<Texture | null> {
    if (this.gone) return null;
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
      await img.decode();
      if (this.gone) return null;
      const texture = new Texture({ source: new ImageSource({ resource: img }) });
      this.textures.set(world, texture);
      this.onLoad(world, texture);
      return texture;
    } catch (err) {
      console.warn('[WorldFloors] a floor did not load:', url, err);
      return null;
    }
  }
}
